import type { GenerationPreviewRow } from './queries'

export type PreviewAction =
  | 'create'
  | 'update'
  | 'unchanged'
  | 'delete'
  | 'keep_manual'
  | 'keep_other'
  | 'keep_approved'

export type PreviewCode = NonNullable<GenerationPreviewRow['currentSalaryCode']>

/** What the rules generate for the row, whatever a run would do with it: a kept
 * row -- manual, approved or not the rules' own -- still shows the rules' value,
 * so an old salary month can be compared against the new generation. */
export function generated(row: GenerationPreviewRow): {
  value: number | null
  code: PreviewCode | null
} {
  return { value: row.generatedValue, code: row.generatedSalaryCode }
}

/** Whether the rules generate something else than what is stored. */
export function differs(row: GenerationPreviewRow): boolean {
  return (
    row.currentValue !== row.generatedValue ||
    (row.currentSalaryCode?.id ?? null) !==
      (row.generatedSalaryCode?.id ?? null)
  )
}

export interface CodeTotal {
  output: string
  code: PreviewCode | null
  now: number
  generated: number
}

/** Month totals per output and salary code, stored and generated. Rounded to
 * thousandths, the precision the rows are stored with. */
export function codeTotals(rows: GenerationPreviewRow[]): CodeTotal[] {
  const totals = new Map<string, CodeTotal>()
  const add = (
    output: string,
    code: PreviewCode | null,
    field: 'now' | 'generated',
    value: number | null
  ) => {
    if (value === null) return
    const key = `${output}:${code?.id ?? ''}`
    const total = totals.get(key) ?? { output, code, now: 0, generated: 0 }
    total[field] = round(total[field] + value)
    totals.set(key, total)
  }
  for (const row of rows) {
    add(row.output, row.currentSalaryCode, 'now', row.currentValue)
    add(row.output, row.generatedSalaryCode, 'generated', row.generatedValue)
  }
  return [...totals.values()].sort(
    (a, b) =>
      a.output.localeCompare(b.output) ||
      (a.code?.name ?? '').localeCompare(b.code?.name ?? '')
  )
}

export function differenceCount(rows: GenerationPreviewRow[]): number {
  return rows.filter(differs).length
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000
}
